// React
import React from 'react'

// Next
import Head from 'next/head'
import Link from 'next/link'
import Image from 'next/image'
import { GetStaticProps } from 'next'

// Components
import CTA from '@/components/website/core/CTA'
import Layout from '@/components/website/core/Layout'
import TextSection from '@/components/website/core/TextSection'
import HeroComp from '@/components/website/core/HeroComp_1'
import HeroComp2 from '@/components/website/core/HeroComp_2'
import FeatureCard from '@/components/website/core/FeatureCard'
import { Button } from '@/components/ui/button'
import LinkedText from '@/components/website/core/LinkedText'

// Utils
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'
import { LinkedContent } from '@/utils/ui/types/linked-content'

// External
import { useTranslation } from 'next-i18next'

const MemeMarketing: React.FC = () => {
  const { t } = useTranslation('routes/meme-marketing')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('meta.title'),
    description: t('meta.description'),
    image: [
      'https://supermeme.ai/static/landing-pages/meme-marketing/hero.png',
      'https://supermeme.ai/static/landing-pages/meme-marketing/meme-marketing-benefits.png',
      'https://supermeme.ai/static/landing-pages/meme-marketing/meme-marketing-uses.png',
    ],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://supermeme.ai/meme-marketing',
    },
    publisher: {
      '@type': 'Organization',
      name: 'SuperMeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
  }

  return (
    <Layout>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta property='og:url' content='https://supermeme.ai/meme-marketing' />
        <link rel='canonical' href='https://supermeme.ai/meme-marketing' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <HeroComp
        mainHeading={t('hero.mainHeading')}
        subHeading={t('hero.subHeading')}
        text={t('hero.text')}
        imageSrc='/static/landing-pages/meme-marketing/hero.png'
      />

      <div className='mx-auto max-w-5xl space-y-8 py-12 sm:py-16'>
        <TextSection
          h2={t('intro.title')}
          p={
            <LinkedText
              content={
                t('intro.description', { returnObjects: true }) as LinkedContent
              }
            />
          }
        />

        <Image
          src='/static/landing-pages/meme-marketing/is-this-meme-marketing.png'
          alt='Is this meme marketing?'
          width={500}
          height={500}
        />

        <TextSection
          p={
            <LinkedText
              content={
                t('intro.followup', { returnObjects: true }) as LinkedContent
              }
            />
          }
        />

        <TextSection
          h2={t('index.title')}
          li={t('index.items', { returnObjects: true }) as string[]}
        />

        <HeroComp2
          subHeading={t('benefits.title')}
          image_src='/static/landing-pages/meme-marketing/meme-marketing-benefits.png'
          benefits={
            t('benefits.items', { returnObjects: true }) as {
              icon: string
              title: string
              description: string
            }[]
          }
        />

        <HeroComp2
          mainHeading={t('uses.mainHeading')}
          subHeading={t('uses.subHeading')}
          image_src='/static/landing-pages/meme-marketing/meme-marketing-uses.png'
          benefits={
            t('uses.items', { returnObjects: true }) as {
              icon: string
              title: string
              description: string
            }[]
          }
          orientation='right'
        />

        <div className='pb-16'>
          <CTA />
        </div>

        <div className='space-y-16 pb-12'>
          <FeatureCard
            mainHeading={t('features.socialMedia.mainHeading')}
            subHeading={t('features.socialMedia.subHeading')}
            screenshot='/static/landing-pages/meme-marketing/social-media.png'
            orientation='left'
          />

          <FeatureCard
            mainHeading={t('features.blogPosts.mainHeading')}
            subHeading={t('features.blogPosts.subHeading')}
            screenshot='/static/landing-pages/meme-marketing/blog-posts.png'
            orientation='right'
          />

          <FeatureCard
            mainHeading={t('features.presentations.mainHeading')}
            subHeading={t('features.presentations.subHeading')}
            screenshot='/static/landing-pages/meme-marketing/presentations.png'
            orientation='left'
          />

          <FeatureCard
            mainHeading={t('features.coldEmails.mainHeading')}
            subHeading={t('features.coldEmails.subHeading')}
            screenshot='/static/landing-pages/meme-marketing/cold-emails.png'
            orientation='right'
          />

          <FeatureCard
            mainHeading={t('features.displayAds.mainHeading')}
            subHeading={t('features.displayAds.subHeading')}
            screenshot='/static/landing-pages/meme-marketing/display-ads.png'
            orientation='left'
          />
        </div>

        <TextSection h2={t('guide.title')} />

        <TextSection
          h2={t('guide.step1.title')}
          p={
            <LinkedText
              content={
                t('guide.step1.description', {
                  returnObjects: true,
                }) as LinkedContent
              }
            />
          }
        />

        <TextSection p={t('guide.step1.followup1')} />

        <TextSection
          p={
            <LinkedText
              content={
                t('guide.step1.followup2', {
                  returnObjects: true,
                }) as LinkedContent
              }
            />
          }
        />

        <TextSection
          h3={t('guide.step2.title')}
          p={t('guide.step2.description')}
        />

        <TextSection
          h3={t('guide.step3.title')}
          p={t('guide.step3.description')}
        />

        <TextSection
          h3={t('guide.step4.title')}
          p={t('guide.step4.description')}
        />

        <TextSection
          h3={t('guide.step5.title')}
          p={
            <LinkedText
              content={
                t('guide.step5.description', {
                  returnObjects: true,
                }) as LinkedContent
              }
            />
          }
        />

        <Image
          src='/static/landing-pages/meme-marketing/search-memes.png'
          alt='Create memes'
          width={1708}
          height={706}
        />

        <TextSection
          h3={t('guide.step6.title')}
          p={t('guide.step6.description')}
        />

        <TextSection h2={t('whatAreTheBenefits.title')} />

        <TextSection
          h3={t('whatAreTheBenefits.part1.title')}
          p={t('whatAreTheBenefits.part1.description')}
        />

        <TextSection
          h3={t('whatAreTheBenefits.part2.title')}
          p={t('whatAreTheBenefits.part2.description')}
        />

        <TextSection
          h3={t('whatAreTheBenefits.part3.title')}
          p={t('whatAreTheBenefits.part3.description')}
        />

        <TextSection
          h3={t('whatAreTheBenefits.part4.title')}
          p={t('whatAreTheBenefits.part4.description')}
        />

        <TextSection
          h2={t('examplesMemeMarketing.title')}
          p={t('examplesMemeMarketing.description')}
        />

        <TextSection
          li={
            t('examplesMemeMarketing.list', { returnObjects: true }) as string[]
          }
        />

        <TextSection
          h2={t('benefitsOfMemeMarketing.title')}
          p={t('benefitsOfMemeMarketing.description')}
        />

        <TextSection
          h3={t('benefitsOfMemeMarketing.costEffective.title')}
          p={t('benefitsOfMemeMarketing.costEffective.description')}
        />

        <TextSection
          h3={t('benefitsOfMemeMarketing.attentionGrabbing.title')}
          p={t('benefitsOfMemeMarketing.attentionGrabbing.description')}
        />

        <TextSection
          h3={t('benefitsOfMemeMarketing.standOutFromTheCrowd.title')}
          p={t('benefitsOfMemeMarketing.standOutFromTheCrowd.description')}
        />

        <TextSection
          h3={t('benefitsOfMemeMarketing.positionYourselfAsAModernBrand.title')}
          p={t(
            'benefitsOfMemeMarketing.positionYourselfAsAModernBrand.description'
          )}
        />

        <TextSection
          h3={t('benefitsOfMemeMarketing.appealToGenZ.title')}
          p={t('benefitsOfMemeMarketing.appealToGenZ.description')}
        />

        <TextSection h2={t('keepInMind.title')} />

        <TextSection
          h3={t('keepInMind.stayRelevant.title')}
          p={t('keepInMind.stayRelevant.description')}
        />

        <TextSection
          h3={t('keepInMind.beAuthentic.title')}
          p={t('keepInMind.beAuthentic.description')}
        />

        <TextSection
          h3={t('keepInMind.knowYourAudience.title')}
          p={t('keepInMind.knowYourAudience.description')}
        />

        <TextSection
          h3={t('keepInMind.useMemesSparingly.title')}
          p={t('keepInMind.useMemesSparingly.description')}
        />

        <TextSection
          h2={t('potentialRisks.title')}
          p={t('potentialRisks.description')}
        />

        <TextSection
          h3={t('potentialRisks.negativeBranding.title')}
          p={t('potentialRisks.negativeBranding.description')}
        />

        <TextSection
          h3={t('potentialRisks.offendingSomeone.title')}
          p={t('potentialRisks.offendingSomeone.description')}
        />

        <TextSection
          h3={t('potentialRisks.outdatedMemes.title')}
          p={t('potentialRisks.outdatedMemes.description')}
        />

        <TextSection h2={t('faqs.title')} />

        <TextSection
          h3={t('faqs.willMemesGoViral.title')}
          p={t('faqs.willMemesGoViral.description')}
        />

        <TextSection p={t('faqs.willMemesGoViral.followup')} />

        <TextSection
          h3={t('faqs.whereCanILearnMemeMarketing.title')}
          p={
            <LinkedText
              content={
                t('faqs.whereCanILearnMemeMarketing.description', {
                  returnObjects: true,
                }) as LinkedContent
              }
            />
          }
        />

        <TextSection
          h3={t('faqs.willIFaceCopyrightIssues.title')}
          p={
            <LinkedText
              content={
                t('faqs.willIFaceCopyrightIssues.description', {
                  returnObjects: true,
                }) as LinkedContent
              }
            />
          }
        />

        <p>
          {t('link.part1')}{' '}
          <Link
            href='/meme-marketing/meme-marketing-platform'
            className='text-indigo-600 hover:underline'
          >
            {t('link.part2')}
          </Link>{' '}
          {t('link.part3')}
        </p>

        <div className='mt-8'>
          <TextSection
            h3={t('callToAction.title')}
            p={t('callToAction.description')}
          />

          <Button asChild>
            <Link href='/login'>{t('callToAction.button')}</Link>
          </Button>
        </div>
      </div>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: ['common', 'routes/meme-marketing', 'components/cta'],
  })
}

export default MemeMarketing
